import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import { CheckCircle2, XCircle, Clock, HelpCircle, ListChecks, Target, Repeat } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";
import { usePageTitle } from "@/lib/title";
import type { Question, Quiz, QuizAttempt } from "@/types";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { FullPageSpinner } from "@/components/ui/Spinner";

interface AnswerState {
  selected: string[];
  text: string;
}

function StatTile({ icon: Icon, label, value }: { icon: typeof Clock; label: string; value: string | number }) {
  return (
    <Card>
      <CardBody className="flex flex-col items-center gap-1.5 py-5 text-center">
        <Icon className="h-5 w-5 text-brand-600" />
        <p className="text-lg font-semibold leading-tight text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </CardBody>
    </Card>
  );
}

export function QuizTakerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [attempt, setAttempt] = useState<QuizAttempt | null>(null);
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [result, setResult] = useState<QuizAttempt | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["quizzes", id],
    queryFn: () => api.get<{ quiz: Quiz; questions: Question[] }>(`/api/v1.0/quizzes/${id}`),
    enabled: !!id,
  });

  const { data: pastAttempts } = useQuery({
    queryKey: ["quizzes", id, "attempts"],
    queryFn: () => api.get<QuizAttempt[]>(`/api/v1.0/quizzes/${id}/attempts`),
    enabled: !!id,
  });
  const finishedAttempts = (pastAttempts ?? []).filter((a) => a.status !== "in_progress");

  const { mutate: startAttempt, isPending: starting } = useMutation({
    mutationFn: () => api.post<QuizAttempt>(`/api/v1.0/quizzes/${id}/attempts`),
    onSuccess: (a) => setAttempt(a),
    onError: (err) => notify(err instanceof ApiError ? err.message : "Failed to start attempt", "error"),
  });

  const { mutate: submitAttempt, isPending: submitting } = useMutation({
    mutationFn: () =>
      api.post<QuizAttempt>(`/api/v1.0/attempts/${attempt!.id}/submit`, {
        answers: Object.entries(answers).map(([questionId, a]) => ({
          question_id: questionId,
          selected_option_ids: a.selected,
          text_answer: a.text,
        })),
      }),
    onSuccess: (a) => {
      setResult(a);
      if (data?.quiz.course_id) {
        void queryClient.invalidateQueries({ queryKey: ["courses", data.quiz.course_id, "modules"] });
      }
      void queryClient.invalidateQueries({ queryKey: ["enrollments", "me"] });
    },
    onError: (err) => notify(err instanceof ApiError ? err.message : "Failed to submit attempt", "error"),
  });

  usePageTitle(data?.quiz?.title);

  if (isLoading || !data) return <FullPageSpinner />;
  const { quiz, questions } = data;

  if (result) {
    return (
      <Card className="mx-auto max-w-2xl">
        <CardBody className="flex flex-col items-center gap-3 py-14 text-center">
          {result.status === "submitted" ? (
            <Clock className="h-12 w-12 text-amber-500" />
          ) : result.passed ? (
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
          ) : (
            <XCircle className="h-12 w-12 text-red-500" />
          )}
          <h2 className="text-xl font-semibold text-slate-900">
            {result.status === "submitted" ? "Submitted for review" : result.passed ? "You passed!" : "Not quite there"}
          </h2>
          {result.score != null && <p className="text-4xl font-bold text-slate-900">{result.score}%</p>}
          <p className="text-sm text-slate-500">
            {result.status === "submitted"
              ? "This quiz contains questions that require manual grading."
              : `Passing score is ${quiz.passing_score}%.`}
          </p>
          <Button onClick={() => navigate(`/courses/${quiz.course_id}`)} className="mt-2">
            Back to course
          </Button>
        </CardBody>
      </Card>
    );
  }

  if (!attempt) {
    const hasTimeLimit = quiz.time_limit_minutes > 0;
    const hasAttemptLimit = quiz.max_attempts > 0;
    // Matches the backend's check in StartAttempt, which counts every attempt
    // row (including any abandoned in-progress one) against the limit.
    const attemptsLeft = hasAttemptLimit ? Math.max(quiz.max_attempts - (pastAttempts?.length ?? 0), 0) : null;

    return (
      <div className="mx-auto flex max-w-2xl flex-col gap-6">
        <Card>
          <CardBody className="flex flex-col items-center gap-3 py-10 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-600">
              <HelpCircle className="h-7 w-7" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900">{quiz.title}</h2>
            {quiz.description && <p className="max-w-md text-sm text-slate-500">{quiz.description}</p>}
          </CardBody>
        </Card>

        <div className="grid grid-cols-3 gap-4">
          <StatTile icon={ListChecks} label="Questions" value={questions.length} />
          <StatTile icon={Clock} label="Time limit" value={hasTimeLimit ? `${quiz.time_limit_minutes} min` : "No limit"} />
          <StatTile icon={Target} label="Passing score" value={`${quiz.passing_score}%`} />
        </div>

        <Card>
          <CardBody>
            <h3 className="mb-3 text-sm font-semibold text-slate-900">Before you start</h3>
            <ul className="flex flex-col gap-2 text-sm text-slate-600">
              <li className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-400" />
                {hasTimeLimit
                  ? `Once started, you have ${quiz.time_limit_minutes} minutes to finish — the timer cannot be paused.`
                  : "Take your time — this quiz has no time limit."}
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-400" />
                You need at least {quiz.passing_score}% correct to pass.
              </li>
              {hasAttemptLimit && (
                <li className="flex gap-2">
                  <Repeat className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                  Limited to {quiz.max_attempts} attempt{quiz.max_attempts > 1 ? "s" : ""} — {attemptsLeft} remaining.
                </li>
              )}
            </ul>
          </CardBody>
        </Card>

        {finishedAttempts.length > 0 && (
          <Card>
            <CardBody>
              <h3 className="mb-3 text-sm font-semibold text-slate-900">Your previous attempts</h3>
              <div className="flex flex-col divide-y divide-slate-100">
                {finishedAttempts.map((a) => (
                  <div key={a.id} className="flex items-center justify-between py-2.5 text-sm">
                    <span className="text-slate-500">{new Date(a.started_at).toLocaleString()}</span>
                    {a.score != null ? (
                      <Badge tone={a.passed ? "green" : "red"}>{a.score}%</Badge>
                    ) : (
                      <Badge tone="amber">Pending review</Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        <Button
          size="lg"
          loading={starting}
          disabled={attemptsLeft === 0}
          onClick={() => startAttempt()}
          className="self-center px-10"
        >
          {attemptsLeft === 0 ? "No attempts remaining" : finishedAttempts.length > 0 ? "Try again" : "Start quiz"}
        </Button>
      </div>
    );
  }

  const isAnswered = (q: Question) =>
    q.type === "short_answer" ? !!answers[q.id]?.text?.trim() : (answers[q.id]?.selected.length ?? 0) > 0;
  const answeredCount = questions.filter(isAnswered).length;

  const scrollToQuestion = (idx: number) => {
    document.getElementById(`question-${idx}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const submitButton = (
    <Button size="lg" loading={submitting} onClick={() => submitAttempt()} className="w-full">
      Submit quiz
    </Button>
  );

  return (
    <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 lg:grid-cols-[1fr_260px] lg:items-start">
      <div className="flex flex-col gap-4">
        {questions.map((q, idx) => (
          <Card key={q.id} id={`question-${idx}`}>
            <CardBody>
              <div className="mb-3 flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700">
                  {idx + 1}
                </span>
                <p className="font-medium text-slate-900">{q.prompt}</p>
              </div>
              {q.type === "short_answer" ? (
                <textarea
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  rows={3}
                  value={answers[q.id]?.text ?? ""}
                  onChange={(e) =>
                    setAnswers((prev) => ({ ...prev, [q.id]: { selected: [], text: e.target.value } }))
                  }
                />
              ) : (
                <div className="flex flex-col gap-2">
                  {q.options.map((opt) => {
                    const isMulti = q.type === "multiple_choice";
                    const selected = answers[q.id]?.selected ?? [];
                    const checked = selected.includes(opt.id);
                    return (
                      <label
                        key={opt.id}
                        className={clsx(
                          "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors",
                          checked ? "border-brand-300 bg-brand-50 text-brand-900" : "border-slate-200 text-slate-700 hover:bg-slate-50",
                        )}
                      >
                        <input
                          type={isMulti ? "checkbox" : "radio"}
                          name={q.id}
                          checked={checked}
                          className="h-4 w-4 shrink-0 accent-brand-600"
                          onChange={() =>
                            setAnswers((prev) => {
                              const current = prev[q.id]?.selected ?? [];
                              const next = isMulti
                                ? checked
                                  ? current.filter((id) => id !== opt.id)
                                  : [...current, opt.id]
                                : [opt.id];
                              return { ...prev, [q.id]: { selected: next, text: "" } };
                            })
                          }
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
              )}
            </CardBody>
          </Card>
        ))}
        <div className="lg:hidden">{submitButton}</div>
      </div>

      <aside className="hidden lg:block">
        <div className="sticky top-6 flex flex-col gap-4">
          <Card>
            <CardBody>
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="font-medium text-slate-700">Progress</span>
                <span className="text-slate-500">
                  {answeredCount}/{questions.length}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-brand-600 transition-all"
                  style={{ width: `${questions.length ? (answeredCount / questions.length) * 100 : 0}%` }}
                />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <p className="mb-3 text-sm font-medium text-slate-700">Questions</p>
              <div className="grid grid-cols-5 gap-2">
                {questions.map((q, idx) => (
                  <button
                    key={q.id}
                    type="button"
                    onClick={() => scrollToQuestion(idx)}
                    title={`Question ${idx + 1}${isAnswered(q) ? " (answered)" : ""}`}
                    className={clsx(
                      "flex h-9 w-9 items-center justify-center rounded-lg text-sm font-medium transition-colors",
                      isAnswered(q) ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200",
                    )}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            </CardBody>
          </Card>

          {submitButton}
        </div>
      </aside>
    </div>
  );
}
